<template>
    <div :class="['totem-select', {'with-icon': icon !== undefined}, { 'squared': squared }, {'darkBorder': darkBorder }, size, type, theme]" :style="cssVars">
        <v-select
            :options="options"
            :placeholder="placeholder"
            @input="handleInput"
            :disabled="disabled"
            :clearable="clearable"
            :closeOnSelect="closeOnSelect"
            :label="label"
            :reduce="reduce"
            :value="value"
            :class="selectorClass"
        >
            <template slot="spinner" slot-scope="option" v-if="icon !== undefined">
                <span :class="'icon-' + icon"></span>
            </template>

            <template v-if="isFilter || isLanguageSelector" v-slot:option="option">
                <template v-if="isFilter">
                    <div class="totem-select__option-filter" @click="removeOption(option)"
                         @mousedown="onMouseDown(option)">
                        <div class="totem-select__checkbox-container">
                            <span class="totem-select__checkbox" v-bind:class="getCheckBoxSelectClass(option)">
                                <span class="totem-select__check-mark"></span>
                                <span class="totem-select__minus-mark"></span>
                            </span>
                        </div>
                        <label class="totem-select__label-filter" v-html="option.label"></label>
                    </div>
                </template>
                <template v-if="isLanguageSelector">
                    <img class="totem-select__language-image" :src="getLanguageImageUrl(option.countryCode)"/>
                    {{ option.label }}
                </template>
            </template>
            <template v-if="isLanguageSelector" #selected-option="option">
                <img class="totem-select__language-image" :src="getLanguageImageUrl(option.countryCode)"/>
                <span class="totem-select__language-label" v-html="option.label"></span>
            </template>
            <span slot="no-options" v-html="$t('TotemSelect.not_found')"></span>
        </v-select>
    </div>
</template>

<script>
    import vSelect from 'vue-select'
    export default {
        name: 'totem-select',
        components: {
            vSelect
        },
        props: {
            reduce: Function,
            label: String,
            isFilter: {
                type: Boolean,
                default: false
            },
            options: Array,
            placeholder: String,
            value: [String, Number, Object],
            size: {
                type: String,
                validator: (prop) => ['small', 'large'].includes(prop),
                default: 'small'
            },
            icon: String,
            isLanguageSelector: {
                type: Boolean,
                default: false
            },
            type: String,
            width: {
                type: [Number, String],
                default: 285
            },
            squared: {
                type: Boolean,
                default: false
            },
            darkBorder: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            },
            clearable: {
                type: Boolean,
                default: true
            },
            closeOnSelect: {
                type: Boolean,
                default: true
            },
            theme: {
                type: String
            }
        },
        data () {
            return {
                content: this.value,
                activeHover: '',
                onMouseDownWasSelected: false
            }
        },
        computed: {
            cssVars () {
                let width
                if (isNaN(this.width)) {
                    width = this.width
                } else {
                    width = this.width + 'px'
                }
                return {
                    '--width': width
                }
            },
            selectorClass () {
                return {
                    'no-options': this.options.length === 0
                }
            }
        },
        methods: {
            handleInput (e) {
                this.$emit('input', e)
            },
            clear () {
                this.$emit('input', null)
            },
            clearWithoutEmit () {
                this.content = null
            },
            onMouseEnter (label) {
                this.activeHover = label
            },
            onMouseLeave () {
                this.activeHover = ''
            },
            onMouseDown (option) {
                this.onMouseDownWasSelected = option.selected
            },
            getCheckBoxSelectClass (option) {
                if (option.selected) {
                    return 'totem-select__checkbox--checked'
                }
                return 'totem-select__checkbox--unchecked'
            },
            removeOption (option) {
                if (option.selected && this.onMouseDownWasSelected) {
                    this.$emit('removeOptionFromFilters', option)
                }
            },
            getLanguageImageUrl (countryCode) {
                return require('assets/images/icons/language-flags/' + countryCode + '.png')
            }
        },
        watch: {
            value (newVal) {
                this.content = newVal
            }
        }
    }
</script>

<style lang="scss">
@import "vue-select/src/scss/vue-select.scss";

.icon-clear {
    color: $carnation;
    margin: 5px;
}

.totem-select {
    width: 100%;
    max-width: var(--width);

    :hover {
        cursor: pointer;
    }

    &.dark {
        .vs__dropdown-toggle {
            border-color: $white;
            background: transparent;
        }

        .vs__selected {
            color: $white;
        }

        .vs__open-indicator {
            fill: $white;
        }

        .vs__dropdown-menu,
        .vs--open {
            .vs__dropdown-toggle {
                border-color: $white;
            }
        }
    }

    &.light {
        .vs__dropdown-toggle {
            border: 1px solid $gray;
        }
    }

    &.squared {
        .vs__dropdown-toggle {
            border-radius: 4px;
        }

        .vs--open {
            .vs__dropdown-toggle {
                border-radius: 4px 4px 0 0;
            }
        }

        .vs__dropdown-menu {
            border-radius: 0 0 4px 4px;
        }
    }

    &.darkBorder {
        .vs__dropdown-toggle {
            border-color: $silver_chalice;
        }
    }

    &__option {
        margin: -3px -20px;
        padding: 3px 20px;
    }

    &__option-filter {
        padding-left: 5px;
        padding-right: 5px;
        display: table;
        table-layout: fixed;
        width: 100%;
    }

    &__label-filter {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: table-cell;
        padding-left: 5px;
        max-width: 100%;
        vertical-align: middle;
        cursor: inherit;
    }

    &__checkbox {
        display: block;
        margin: auto;
        width: 12px;
        height: 12px;
        border-width: 1px;
        border-style: solid;
        border-radius: 2px;
        position: relative;
        -webkit-transition: 200ms all cubic-bezier(0.075, 0.82, 0.165, 1);
        transition: 200ms all cubic-bezier(0.075, 0.82, 0.165, 1);
    }

    &__checkbox--checked {
        opacity: 1;
        -webkit-transform: scaleY(1);
        transform: scaleY(1);
        border-color: $orange;
        background: $orange;
    }

    &__checkbox--unchecked {
        border-color: $alto;
        background: $white;
    }

    &__checkbox--checked > &__check-mark {
        opacity: 1;
        -webkit-transform: scaleY(1);
        transform: scaleY(1);
    }

    &__checkbox-container {
        display: table-cell;
        width: 20px;
        min-width: 20px;
        height: 100%;
        text-align: center;
        vertical-align: middle;
    }

    &__check-mark {
        width: 8px;
        height: 8px;
        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAMAAADz0U65AAAAQlBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////8IX9KGAAAAFXRSTlMA8u24NxILB+Tawb6jiH1zRz0xIQIIP3GUAAAAMklEQVQI1y3FtQEAMQDDQD+EGbz/qkEVOpyEOP6PudKjZNSXn4Jm2CKRdBKzSLsFWl8fMG0Bl6Jk1rMAAAAASUVORK5CYII=);
        background-size: 8px 8px;
        -webkit-transform: scaleY(0.125);
        transform: scaleY(0.125);
    }

    &__check-mark,
    &__minus-mark {
        display: block;
        position: absolute;
        left: 1px;
        top: 1px;
        background-repeat: no-repeat;
        opacity: 0;
        -webkit-transition: 200ms all ease;
        transition: 200ms all ease;
    }

    &__language-image {
        margin: 0 5px -2px 0;
    }

    &__language-label {
        display: none;

        [class*="desktop_xx"] & {
            display: inline-block;
        }
    }

    &.-margin-left {
        margin: 10px 0 0;

        [class*="desktop"] & {
            margin: 0 0 0 10px;
        }
    }

    .vs__dropdown-toggle {
        background: $white;
        border: 1px solid $quill_gray;
        border-radius: 30px;
    }

    .vs--open {
        .vs__dropdown-toggle {
            border-radius: 15px 15px 0 0;
            border-color: $orange;
        }
    }

    .vs__dropdown-menu {
        border: 1px solid $orange;
        border-radius: 0 0 15px 15px;
        min-width: 150px;
    }

    .vs__state-active-bg {
        background: $orange;
    }

    .vs__dropdown-option {
        white-space: normal;
        padding: 3px 7px;
    }

    .vs__dropdown-option:hover {
        background: $wild_sand;
    }

    .vs__dropdown-option--highlight {
        background: none;
        color: $black;
    }

    .vs__selected {
        color: $black;
        width: 100%;
    }

    .vs__selected-options {
        padding-left: 8px;
        height: 28px;
        overflow: hidden;
    }

    .vs--single .vs__selected {
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        white-space: nowrap;
    }

    .vs__actions span {
        position: absolute;
        left: 8px;
    }

    .vs--disabled {
        .vs__dropdown-toggle {
            background: $alabaster;
        }

        .vs__selected {
            color: $emperor;
        }
    }

    .vs__clear {
        line-height: 1px;
        fill: $black;
    }

    .vs__open-indicator {
        fill: $black;
    }

    &.sorting {
        .vs__dropdown-toggle {
            border: 1px solid 1px solid $quill_gray;
        }

        .vs__dropdown-menu {
            border: 1px solid 1px solid $quill_gray;
        }

        .vs__dropdown-option--highlight {
            background: 1px solid $quill_gray;
        }
    }

    &.small {
        font-size: 1.6rem;

        .vs__dropdown-toggle {
            padding: 0 6px 4px 0;
        }

        .vs__selected {
            margin: 6px 2px 0 2px;
        }

        .vs__selected-options {
            height: 32px;
        }
    }

    &.large {
        font-family: Nunito-Bold, sans-serif;
        font-size: 2.2rem;

        .vs__dropdown-toggle {
            padding: 0 15px 5px 5px;
        }

        .vs__selected {
            margin: 7px 2px 0 2px;
        }

        .vs__selected-options {
            height: 43px;
        }

        .vs__search {
            font-family: Nunito-Bold, sans-serif;
        }
    }

    &.with-icon {
        .vs__selected-options {
            padding-left: 25px;
        }
    }

    &.fi-authorizations {
        .vs__dropdown-menu,
        .vs__selected {
            text-transform: uppercase;
        }
    }

    .no-options {
        cursor: not-allowed;

        .vs__dropdown-toggle  {
            pointer-events: none;
            opacity: 0.6;
            background: $concrete;
        }
    }
}

</style>
